import { Button, Card, CardHeader, CardPreview, Dropdown, Option, Table, TableCell, TableRow, Text } from "@fluentui/react-components";
import { LogComponent, LogElement } from "../../../models/LogModel";
import React, { useCallback, useMemo, useState } from "react";
import { buildChartSeries, classifyCategory, getAllMonthsBetween, getCurrentMonth, getFiscalYearRange, getPreviousMonth, getUTCMonthStartDate } from "../../../utils/BudgetUtils";

import { ArrowCircleLeft24Filled } from "@fluentui/react-icons";
import { BudgetLineChart } from "../Common/BudgetLineChart";
import { BudgetOverviewCards } from "../Common/BudgetOverviewCard";
import GEMDrillDownMenu from "../Common/GEMDrilldownMenu";
import Highcharts from "highcharts";
import { HighchartsReact } from "highcharts-react-official";
import { IFinOpsBudgetAllocationCombined } from "../../../models/FinOpsBudgets";
import ServiceLayout from "./ServiceLayout";
import SummaryTable from "../Common/SummaryTable";
import { currencyFormatter } from "../../../utils/currency";
import styles from "./GemLayout.less";
import { useGetFyBaseline } from "../../../hooks/useFinOpsBudget";

interface GemLayoutProps {
  gem: string;
  data: IFinOpsBudgetAllocationCombined[];
  isLoadingBudgetData: boolean;
  currentFiscalYear: string;
}

const GemLayout: React.FC<GemLayoutProps> = ({ gem, data, isLoadingBudgetData, currentFiscalYear }) => {
  const fiscalYears = Array.from({ length: parseInt(currentFiscalYear.slice(2)) - 26 + 1 }, (_, i) => `FY${26 + i}`);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(currentFiscalYear);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  const { start: fiscalStart, end: fiscalEnd } = React.useMemo(() => getFiscalYearRange(selectedFiscalYear), []);
  const allMonths = getAllMonthsBetween(fiscalStart, fiscalEnd);
  const currentMonth = getCurrentMonth();
  const lastMonth = getPreviousMonth();
  const previousFiscalYear = `FY${parseInt(selectedFiscalYear.slice(2)) - 1}`;
  const { start: prevFiscalStart, end: prevFiscalEnd } = React.useMemo(() => getFiscalYearRange(previousFiscalYear), []);
  const { data: goldenBaseline = [], isLoading, error } = useGetFyBaseline({ fiscalYear: previousFiscalYear });
  const filteredBaseline = useMemo(() => {
    return goldenBaseline.filter(d => {
      if (!d.gem) {
        return false;
      }
      
      return d.gem === gem;
    });
  }, [goldenBaseline, gem]);

  const filteredData = useMemo(() => {
    return data.filter(d => {
      if (!d.month) {
        return false;
      }
      const inSelectedService = selectedServices.length === 0 || (d.serviceName && selectedServices.includes(d.serviceName));
      return inSelectedService;
    });
  }, [data, selectedServices]);

  const services = useMemo(() => {
    return Array.from(
      new Set(
        data.map(d => d.serviceName)
      )
    ).filter((service): service is string => service !== undefined);
  }, [data]);

  const [selectedServiceDrilldown, setSelectedServiceDrilldown] = useState<string | null>(null);

  const handleServiceDrilldown = useCallback((service: string) => {
    setSelectedServiceDrilldown(service);
  }, []);
 
  const series = buildChartSeries(filteredData, currentMonth);

  const categories = useMemo(() => ["Azure", "Cosmic", "MCIO Network", "Passive Monitoring"], []);
  const categoryColors = useMemo<Record<string, string>>(() => ({
    "Azure": "#aec7e8",
    "Cosmic": "#ffbb78",
    "MCIO Network": "#98df8a",
    "Passive Monitoring": "#dadaeb",
  }), []);

  const sortedServiceStats = useMemo(() => {
    return services.map(service => {
      const serviceItems = filteredData.filter(d => d.serviceName === service && d.month);

      const costByCategory: Record<string, number> = {};
      categories.forEach(cat => {
        const cost = serviceItems
          .filter(d => classifyCategory(d) === cat)
          .reduce((sum, d) => sum + (d.cost ?? 0), 0);
        costByCategory[cat] = cost;
      });

      const totalCost = Object.values(costByCategory).reduce((sum, v) => sum + v, 0);
      const totalBudget = serviceItems.reduce((sum, d) => sum + (d.budget ?? 0), 0);

      return { service, costByCategory, totalCost, budget: totalBudget, gap: totalCost - totalBudget };
    });
  }, [services, filteredData, categories]);
  

  const displayedService = useMemo(() => (
    selectedServiceDrilldown ? [selectedServiceDrilldown] : services
  ), [selectedServiceDrilldown, services]);

  const displayedServiceStats = useMemo(() => (
    sortedServiceStats
      .filter(s => displayedService.includes(s.service))
      .sort((a, b) => b.totalCost - a.totalCost)
      .slice(0, 10)
  ), [sortedServiceStats, displayedService]);

  const serviceBarCategories = useMemo(() => (
    displayedServiceStats.map(s => s.service)
  ), [displayedServiceStats]);

  const stackedCostSeries = useMemo<Highcharts.SeriesOptionsType[]>(() => {
    return categories.map(cat => ({
      name: cat,
      type: "bar" as const,
      stack: "cost",
      data: displayedServiceStats.map(g => g.costByCategory[cat] || 0),
      grouping: false,
      pointPlacement: 0.2,
      color: categoryColors[cat],
    }));
  }, [categories, displayedServiceStats, categoryColors]);

  const budgetSeries = useMemo<Highcharts.SeriesOptionsType>(() => ({
    name: "Budget",
    type: "bar" as const,
    data: displayedServiceStats.map(g => g.budget),
    color: "#eeeeee",
    stack: "budget",
    grouping: false,
    pointPlacement: -0.2,
  }), [displayedServiceStats]);

  const serviceBarChartOptions = React.useMemo<Highcharts.Options>(() => ({
    chart: {
      type: "bar",
      height: serviceBarCategories.length * 50 + 600
    },
    title: {
      text: `${selectedFiscalYear} Service Budget vs Cost Breakdown`
    },
    legend: {
        align: "center",
        verticalAlign: "top",
        layout: "horizontal",
        itemStyle: {
          fontWeight: "normal",
          fontSize: "12px"
        }
    },
    xAxis: {
      categories: serviceBarCategories,
      title: { text: "Service" },
      labels: {
        style: { fontSize: "12px", fontWeight: "bold" }
      }
    },
    yAxis: {
      min: 0,
      title: { text: "Amount ($)" },
      labels: {
        formatter() {
          return currencyFormatter(this.value as number);
        }
      }
    },
    tooltip: {
      shared: true,
      formatter: function () {
        const service = this.x as unknown as string;
        const totalCost = this.points?.filter(p => p.series.userOptions.stack === "cost")
          .reduce((sum, p) => sum + (p.y ?? 0), 0) ?? 0;
        const budget = this.points?.find(p => p.series.name === "Budget")?.y ?? 0;
        return `
          <b>${service}</b><br/>
          Cost: ${currencyFormatter(totalCost)}<br/>
          Budget: ${currencyFormatter(budget)}<br/>
          Gap: ${currencyFormatter(totalCost - budget)}
        `;
      }
    },
    plotOptions: {
      bar: {
        stacking: "normal",
        pointWidth: 28,
        groupPadding: 0.3,
        pointPadding: 0.2,
        states: {
          hover: {
            enabled: true,
            brightness: -0.2
          }
        },
        dataLabels: {
          enabled: false
        },
        events: {
          click: function (event) {
            const service = event.point.category;
            handleServiceDrilldown(service);
          },
        },
      }
    },
    series: [...stackedCostSeries, budgetSeries],
    credits: { enabled: false }
  }), [selectedFiscalYear, serviceBarCategories, stackedCostSeries, budgetSeries, handleServiceDrilldown]);

  const pieChartOptions = React.useMemo<Highcharts.Options | null>(() => {
    if (!selectedServiceDrilldown) return null; 
    return {
      chart: {
        type: "pie",
        height: 400,
      },
      title: {
        text: `${selectedServiceDrilldown} Cost Breakdown by Category`,
      },
      tooltip: {
        pointFormat: '<b>{point.name}</b>: {point.y:.2f}',
          formatter: function () {
            const point = this.point;
            return `<b>${point.name}</b>: ${currencyFormatter(point.y ?? 0)}`;
          }
      },
      accessibility: {
        point: {
          valueSuffix: "%",
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
            format: `<b>{point.name}</b>: {point.percentage:.1f} %`,
          },
        },
      },
      series: [
        {
          name: "Cost",
          type: "pie",
          data: Object.entries(
            displayedServiceStats.find(s=> s.service === selectedServiceDrilldown)?.costByCategory || {}
          ).map(([cat, value]) => ({
            name: cat,
            y: value,
            color: categoryColors[cat],
          })),
        } as Highcharts.SeriesPieOptions,
      ],
    };
  }, [selectedServiceDrilldown, displayedServiceStats, categoryColors]);

  return (
    <div className={styles.serviceLayout}>
        <h1 className={styles.layoutTitle}>{gem}</h1>
        <p className={styles.description}>
          Budget vs Actuals Tracking offers a comprehensive view of budget tracking for {`${currentFiscalYear}`}, helping you monitor budget vs actuals from multiple dimensions.
        </p>

        <div className={styles.dropdownContainer}>
          <div className={styles.dropdownGroup}>
            <label id={"Select Fiscal Year"}>FiscalYear</label>
            <Dropdown
              aria-labelledby={"Select Fiscal Year"}
              placeholder="Choose a fiscal year"
              defaultValue={selectedFiscalYear}
              value={selectedFiscalYear}
              onOptionSelect={(_, data) => {
                if (data.optionValue) {
                  setSelectedFiscalYear(data.optionValue);
                }
              }}>
              {fiscalYears.map((option) => (
                <Option key={option}>
                  {option}
                </Option>
              ))}
            </Dropdown>
          </div>
          <div className={styles.dropdownGroup}>
            <label id={"Select Services"}>Services</label>
            <Dropdown
              aria-labelledby={"Select Services"}
              multiselect
              className={styles.truncatedOption}
              placeholder="Select services"
              selectedOptions={selectedServices.includes("__ALL__") ? ["__ALL__"] : selectedServices}
              onOptionSelect={(_, data) => {
                const value = data.optionValue as string;
                if (value === "__ALL__") {
                  if (selectedServices.length === services.length) {
                    setSelectedServices([]);
                  } else {
                    setSelectedServices(services);
                  }
                } else {
                  const nextSelected = data.selectedOptions.filter(v => v !== "__ALL__") as string[];
                  setSelectedServices(nextSelected);
                }
              }}
            >
              <Option value="__ALL__">
                {selectedServices.length === services.length ? "Deselect All" : "Select All"}
              </Option>

              {services.map(service => (
                <Option key={service} value={service}>
                    {service}
                </Option>
              ))}
            </Dropdown>
          </div>
        </div>

        {/* Cards in a row */}
        <BudgetOverviewCards
          currentFiscalYear={selectedFiscalYear}
          previousFiscalYear={previousFiscalYear}
          currentMonth={currentMonth}
          data={filteredData}
          baselineData={filteredBaseline}
          isLoadingBudgetData={isLoadingBudgetData}
          currencyFormatter={currencyFormatter}
          viewType="overview"
        />

        <SummaryTable
          previousFiscalYear={previousFiscalYear}
          currentFiscalYear={currentFiscalYear}
          currentMonth={currentMonth}
          data={filteredData}
          baselineData={filteredBaseline}
          isLoadingBudgetData={isLoadingBudgetData}
        />

        {/* Line Chart */}
        <BudgetLineChart
          title="Budget vs Actuals"
          isLoading={isLoadingBudgetData}
          series={series}
          logProps={{
            logComponent: LogComponent.BudgetTracking,
            logElement: LogElement.BudgetTrackingServiceLayout,
            customProperties: {}
          }}
          fillMissingAbnormalData
          anomalies={[]}
          suffix=""
          hideLegend={true}
        />

        <div className={styles.heatmapWrapper}>
          <GEMDrillDownMenu gems={services} data={filteredData} onGEMDrilldownSelect={({ gem }) => handleServiceDrilldown(gem)}/>
          {selectedServiceDrilldown && (
            <>
              <Button onClick={() => setSelectedServiceDrilldown(null)} appearance="primary" icon={<ArrowCircleLeft24Filled/>} style={{ marginLeft: "10px"}}>Back to Top 10 Services</Button>
              <BudgetOverviewCards
                currentFiscalYear={selectedFiscalYear}
                previousFiscalYear={previousFiscalYear}
                currentMonth={currentMonth}     
                data={filteredData.filter(d => d.serviceName === selectedServiceDrilldown)}
                baselineData={filteredBaseline.filter(d => d.serviceName === selectedServiceDrilldown)}
                isLoadingBudgetData={isLoadingBudgetData}
                currencyFormatter={currencyFormatter}
                viewType="detail"
              />
            </>
          )}  
          {selectedServiceDrilldown ? (
            <div style={{ marginTop: "40px" }}>
              <HighchartsReact
                highcharts={Highcharts}
                options={pieChartOptions!}
              />
            </div>
          ) : (
            <div style={{ marginTop: "40px" }}>
              <HighchartsReact
                highcharts={Highcharts}
                options={serviceBarChartOptions}
              />
            </div>
          )}
        </div>
    </div>
  );}

export default GemLayout;
